import { practiceLessonMap } from "@circleofa/acim-engine"
import type { PracticeTimeValues } from "@circleofa/acim-engine/dist/book/content/allPractice"
import { NotificationAtType } from "@utils/cap-notification-manager/cap-notification-manager"
import { expectNeverThrow } from "@utils/utilities/never"
import { simpleHash } from "@utils/utilities/simple-hash"
import { times } from "lodash-es"

// @ts-expect-error debug
globalThis.practiceLessonMap = practiceLessonMap

// export type TaskNotificationPackage = {

// }

export interface LessonTime {
    readonly atType: NotificationAtType
    readonly individualMovability: number
    readonly collectiveMovability: number
    /**
     *
     * @param start Now / wakeup time (whichever is later) - minutes from midnight
     * @param end Sleep time - minutes from midnight
     */
    getMinutes(start: number, end: number): number[]
}

export function isLessonTime(o: unknown): o is LessonTime {
    return !!(
        o &&
        typeof o === "object" &&
        "individualMovability" in o &&
        "collectiveMovability" in o &&
        "getMinutes" in o
    )
}

/**
 * Has 0 flexibility, used when lessons say things like
 * "on the hour exactly on the hour" type thing
 */
export class LessonTimeExactMinute implements LessonTime {
    readonly atType = NotificationAtType.EXACT_REPEATING

    public readonly individualMovability = 0
    public readonly collectiveMovability = 0
    get minutesOfHour() {
        switch (this.descriptionOfMinutes) {
            case "hour":
                return [0]
                break
            case "half":
                return [30]
                break
            case "both":
                return [0, 30]
        }
    }
    constructor(public readonly descriptionOfMinutes: "hour" | "half" | "both") {}

    getMinutes(start: number, end: number): number[] {
        const nums = []
        for (let i = start; i <= end; i++) {
            const minuteOfHour = i % 60
            if (this.minutesOfHour.includes(minuteOfHour)) nums.push(i)
        }
        return nums
    }
}

export enum TimesPerDaySpacing {
    SPACE_BETWEEN,
    SPACE_AROUND,
}

/**
 * These are generally the main lessons and are a handful per day
 * at specific times, it makes sense to break them up into
 * separate notifications
 */
export class LessonTimesPerDay implements LessonTime {
    readonly atType = NotificationAtType.EXACT

    public readonly individualMovability = Infinity
    public readonly collectiveMovability = 0
    /**
     *
     * @param timesPerDay Default times per day
     */
    constructor(
        public readonly timesPerDay: number,
        public readonly spacing: TimesPerDaySpacing,
    ) {}

    getMinutes(start: number, end: number): number[] {
        const delta = end - start
        switch (this.spacing) {
            case TimesPerDaySpacing.SPACE_BETWEEN: {
                if (this.timesPerDay === 1) return [start]
                const each = Math.floor(delta / (this.timesPerDay - 1))
                return times(this.timesPerDay, (i) => start + i * each)
            }
            case TimesPerDaySpacing.SPACE_AROUND: {
                const each = Math.floor(delta / (this.timesPerDay + 1))
                return times(this.timesPerDay, (i) => start + each + i * each)
            }
            default:
                throw new Error("Spacing not right")
        }
    }
}

export class LessonTimesPerHour implements LessonTime {
    readonly atType = NotificationAtType.REPEATING

    public readonly individualMovability = 0
    public readonly collectiveMovability = Infinity
    /**
     *
     * @param timesPerHour Default times per day
     */
    constructor(public readonly timesPerHour: number) {}

    get minutesBetweenNotifications() {
        return 60 / this.timesPerHour
    }

    getMinutes(start: number, end: number): number[] {
        const nums: number[] = []
        let i = start
        while (i <= end) {
            nums.push(i)
            i += this.minutesBetweenNotifications
        }
        return nums
    }

    /**
     * Given the times per hour, emits the respective time
     * values for the first hour (or the minute value of every hour)
     * @param offset
     * @returns
     */
    minutesInEachHour(offset = 0): number[] {
        if (offset >= 60) return []
        return [offset, ...this.minutesInEachHour(offset + this.minutesBetweenNotifications)]
    }
}

//
// Generators are responsible for plopping out LessonTime classes
// based on the desires of the users
//

/**
 * The generator that allows per day X amount
 * Is able to pop out {@link LessonTime}s based on the given options
 */
export class PerDayLessonGenerator {
    readonly atType = NotificationAtType.EXACT
    constructor(
        public readonly timesPerDay: number,
        public readonly timesPerDayMin: number,
        public readonly timesPerDayMax: number,
        public readonly spacing: TimesPerDaySpacing = TimesPerDaySpacing.SPACE_BETWEEN,
    ) {}

    get defaultOption() {
        return this.timesPerDay
    }

    /**
     * Returns all the possible timesPerDays, e.g. [4,5,6] (4, 5, or 6 times per day allowed)
     */
    get options() {
        return new Set(
            Array.from(
                { length: this.timesPerDayMax - this.timesPerDayMin + 1 },
                (v, k) => k + this.timesPerDayMin,
            ),
        )
    }

    /**
     * Generates a lesson time based on the given option
     * @param option option from {@link this.options} list
     * @returns
     * @throws if its a bad number
     */
    generateFromOption(option: number): LessonTimesPerDay {
        if (!this.options.has(option))
            throw new Error(`Invalid option ${option} out of possible options`)
        return new LessonTimesPerDay(option, this.spacing)
    }

    get signature() {
        return simpleHash(
            "day" +
                this.timesPerDay +
                " " +
                this.timesPerDayMin +
                " " +
                this.timesPerDayMax +
                " " +
                this.spacing,
        )
    }
}

export class PerHourLessonGenerator {
    readonly atType = NotificationAtType.REPEATING

    constructor(
        public readonly timesPerHour: number,
        public readonly timesPerHourMin: number,
        public readonly timesPerHourMax: number,
    ) {}

    get defaultOption() {
        return this.timesPerHour
    }

    /**
     * Returns all the possible timesPerHour, e.g. [4,5,6] (4, 5, or 6 times per hour allowed)
     */
    get options() {
        return new Set(
            times(1 + this.timesPerHourMax - this.timesPerHourMin, (d) => d + this.timesPerHourMin),
        )
    }

    /**
     * Generates a lesson time based on the given option
     * @param option option from {@link this.options} list
     * @returns
     */
    generateFromOption(option: number): LessonTimesPerHour {
        if (!this.options.has(option))
            throw new Error(
                `Invalid option ${option} out of possible options ${JSON.stringify(this.options)}`,
            )
        return new LessonTimesPerHour(option)
    }

    get signature() {
        return simpleHash(
            "hour" + this.timesPerHour + " " + this.timesPerHourMin + " " + this.timesPerHourMax,
        )
    }
}

export function isGenerator(o: unknown): o is PerDayLessonGenerator | PerHourLessonGenerator {
    return o instanceof PerDayLessonGenerator || o instanceof PerHourLessonGenerator
}

function timesPerDayFlexibleEvenlySpaced(timesPerDay: number) {
    return new LessonTimesPerDay(timesPerDay, TimesPerDaySpacing.SPACE_BETWEEN)
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function morningEvening(preferably = false) {
    // Ignoring preferably because that will be in the text for the user
    return new LessonTimesPerDay(2, TimesPerDaySpacing.SPACE_BETWEEN)
}

function timesPerDayFlexibleToInfinity(timesPerDay: number) {
    return new PerDayLessonGenerator(
        timesPerDay,
        timesPerDay,
        timesPerDay + 3,
        TimesPerDaySpacing.SPACE_BETWEEN,
    )
}

/**
 * Allows for flexiblity
 * @param every
 */
function individuallyFlexiblePerHour(perHourNum: number) {
    return perHour(perHourNum, perHourNum, perHourNum)
}

function earlyLateAndBetween() {
    return new LessonTimesPerDay(3, TimesPerDaySpacing.SPACE_BETWEEN)
}

function perHour(min: number, max: number, def: number, toInfinity = false) {
    // Subbing to rigid version because we don't care to move individual things
    // anyway
    return perHourRigid(min, max, def, toInfinity)
}

function perHourRigid(min: number, max: number, def: number, toInfinity = false) {
    if (min === max && def === max) {
        if (toInfinity) {
            return new LessonTimesPerHour(def)
        } else {
            return new PerHourLessonGenerator(def, def, def + 3)
        }
    } else {
        const newMax = toInfinity ? max + 3 : max
        return new PerHourLessonGenerator(def, min, newMax)
    }
}

function perDay(min: number, max: number, def: number, toInfinity = false) {
    if (min === max && max === def && toInfinity === false) {
        return new LessonTimesPerDay(def, TimesPerDaySpacing.SPACE_BETWEEN)
    }
    return new PerDayLessonGenerator(
        def,
        min,
        toInfinity ? max + 3 : max,
        TimesPerDaySpacing.SPACE_BETWEEN,
    )
}

function afap(perHourSuggestion: number = 4) {
    return new PerHourLessonGenerator(perHourSuggestion, 1, 6)
}

function once() {
    return new LessonTimesPerDay(1, TimesPerDaySpacing.SPACE_AROUND)
}

function preselect(num: 1 | 2) {
    return new LessonTimesPerDay(num, TimesPerDaySpacing.SPACE_AROUND)
}

function earlyMorning() {
    return new LessonTimesPerDay(1, TimesPerDaySpacing.SPACE_BETWEEN)
}

function none() {
    return null
}

function onExactMinuteOnInHour(minutesOnHour: "half" | "hour" | "both") {
    return new LessonTimeExactMinute(minutesOnHour)
}

/**
 * Make sure to catch!
 * @param t
 * @returns null if no notification
 * @throws if not a valid time descriptor
 */
export function timeCrunchMachine(
    t: PracticeTimeValues,
):
    | LessonTimeExactMinute
    | LessonTimesPerDay
    | LessonTimesPerHour
    | PerDayLessonGenerator
    | PerHourLessonGenerator
    | null
    | "Temptation" {
    switch (t) {
        case "1 pre-selected time":
            return preselect(1)
        case "1, maybe 2, per hour":
            return perHour(1, 2, 1)
        case "1 time—whenever it seems best":
            return once()
        case "2 per hour":
            return perHour(2, 2, 2)
        case "2 pre-selected times":
        case "2 times (at predetermined times of your choosing)":
        case "2 times (at prescheduled times)":
            return preselect(2)
        case "2 times":
        case "2 times (once for each of the ideas)":
            return perDay(2, 2, 2)
        case "2 times—(early) morning and (late) evening":
        case "2 times—morning and evening":
        case "2 times—at beginning and end of day":
        case "2 times—beginning and ending of the day":
        case "2 times—the first and last moments of your day":
        case "One in the morning, one in the hour before sleep (ideally the first and last 5 minutes of your day)":
            return morningEvening()
        case "2 times—ideally, morning and evening":
        case "2 times—morning and evening preferably":
            return morningEvening(true)
        case "3 or 4 (at least 3)":
        case "3 or 4 times (3 are required)":
        case "3 times (4 if comfortable)":
            return perDay(3, 4, 3)
        case "3 or 4 times":
        case "3 or 4 times (no more)":
            return perDay(3, 4, 4)
        case "3 times":
        case "3 times (at times best suited to silence)":
            return perDay(3, 3, 3)
        case "3 times (4 or 5 if you find that comfortable and desirable)":
            return perDay(3, 5, 3)
        case "3 times (unless you find more comfortable)":
            return timesPerDayFlexibleToInfinity(3)
        case "3 times per hour, at least":
            return perHour(3, 3, 3, true)
        case "3 times—morning (early as possible), evening (late as possible), and in between (when readiness and circumstances permit)":
        case "3 times—morning, evening, and once in between":
            return earlyLateAndBetween()
        case "4 or 5 per hour, maybe more if possible":
            return perHour(4, 5, 4, true)
        case "4 or 5 times (3 if there’s strain)":
        case "4 or 5 times (3 or 4 if you find the practice irritating)":
            return perDay(3, 5, 4)
        case "4 times":
            return perDay(4, 4, 4)
        case "4 times (evenly spaced out)":
            return timesPerDayFlexibleEvenlySpaced(4)
        case "4 times (more are encouraged)":
        case "4 times (more are urged)":
        case "4 times (more if possible)":
            return timesPerDayFlexibleToInfinity(4)
        case "4 or 5 per hour (at least)":
            return perHour(4, 5, 4, true)
        case "5 or 6 per hour, at fairly regular intervals":
            return perHourRigid(5, 6, 5)
        case "5 times":
            return perDay(5, 5, 5)
        case "5 times (at least)":
            return perDay(5, 5, 5, true)
        case "6 or 7 per hour":
            return perHour(6, 7, 6)
        case "6 times":
            return perDay(6, 6, 6)
        case "As frequently as possible":
        case "As many as possible":
        case "As often as possible":
        case "As often as possible throughout the day (you might want set a frequency)":
        case "As often as possible within each hour":
        case "As often as you can":
        case "As often as you can (do not forget for long)":
        case "Frequency is important today":
        case "Frequently":
        case "Often":
        case "Often—be determined not to forget":
        case "Very frequently, as often as possible":
        case "Very frequently":
            return afap()
        case "As often as possible (suggestion: at least twice per hour)":
            return afap(2)
        case "As often as possible (suggestion: every hour)":
            return afap(1)
        case "As soon as possible after rising":
        case "As you arise":
            return earlyMorning()
        case "As the hour strikes":
        case "Every hour on the hour":
            return onExactMinuteOnInHour("hour")
        case "At least 1":
            return perDay(1, 1, 1, true)
        case "At least 1 per hour":
            return perHour(1, 1, 1, true)
        case "At least 3 or 4 per hour":
            return perHour(3, 4, 3, true)
        case "At least 3 times":
            return perDay(3, 3, 3, true)
        case "At least every half hour (3 or 4 times an hour is encouraged) — set a definite interval at the start of the day and try to adhere to it":
            return perHour(2, 4, 2, true)
        case "Do not forget today":
        case "In everything you do":
            return none()
        case "Every 10 minutes is highly desirable":
            return perHour(6, 6, 6)
        case "Every 15 minutes":
            return perHour(4, 4, 4)
        case "Every hour":
        case "Every hour (do not forget)":
        case "Hourly":
        case "Once per hour":
            return perHour(1, 1, 1)
        case "In between hourly practice periods":
            return onExactMinuteOnInHour("half")
        case "On the hour and on the half hour":
            return onExactMinuteOnInHour("both")
        case "Several (roughly 3-5) per hour":
            return perHour(3, 5, 4)
        case "Several per hour":
            return perHour(3, 5, 4)
        case "Twice per hour (preferably every half hour)":
            return perHour(2, 2, 2, false)
        case "At least once an hour":
            return perHour(1, 1, 1, true)
        case "At regular intervals that you predetermine (he suggests every half hour)":
            return perHour(1, 4, 2, false)

        case "Optional—when you have a thought you recognize as harmful":
        case "Optional—when you experience upset":
        case "Optional—when you experience disturbance":
        case "Optional—whenever you have a distressing thought":
        case "Optional—when anything disturbs you":
        case "Optional—whenever you are upset":
        case "Whenever you are aware of an upsetting thought":
        case "In response to any unwanted thought":
        case "Whenever you become upset about a person, situation, or event":
        case "Whenever a situation arouses anger":
        case "Whenever you notice yourself having attack thoughts":
        case "When you feel like anything in the world is victimizing you":
        case "Whenever a situation upsets you":
        case "The instant any situation disturbs you":
        case "Whenever you experience an upset. Make a point of watching your mind today for these.":
        case "Whenever you have an adverse reaction to someone":
        case "Whenever a specific problem \u2014 your own or someone else\u2019s \u2014 presents itself or comes to mind":
        case "Whenever you are tempted to give in to unloving thoughts":
        case "Whenever you have fear thoughts":
        case "The more often the better":
        case "Whenever you get distressed about an event or situation":
        case "When you have a negative reaction to anyone, \u201Cpresent or not\u201D (7:3)":
        case "When any disturbance arises":
        case "When anything disturbs your peace of mind":
        case "Whenever you feel confronted by a problem or challenge":
        case "Whenever you are tempted to believe you are not the light of the world":
        case "Whenever you feel a grievance against anyone":
        case "Whenever you are tempted to hold a grievance":
        case "Whenever you are tempted to be upset with someone":
        case "Whenever you feel subject to the laws of this world":
        case "Every time you meet, think of, or remember someone":
        case "Whenever you see a problem":
        case "Whenever a specific problem arises, especially an interpersonal one":
        case "Whenever special difficulties seem to arise":
        case "Whenever you are tempted to be upset":
        case "Whenever a situation or person tempts you to be upset":
        case "Whenever someone seems to irritate you":
        case "Whenever you are tempted to yield to the belief that you are not spirit":
        case "Whenever some appearance tempts you to be give in to fear and doubt":
        case "Whenever you feel any form of fear":
        case "Whenever you are tempted to deny God\u2019s gift to someone":
        case "Whenever you face a problem or experience suffering":
        case "Whenever your peace is shaken":
        case "Whenever you feel yourself valuing something in the world":
        case "Whenever you find yourself valuing anything in the world":
        case "If you forget what a special day it is and fall into depression or complaining":
        case "Whenever you believe that your thoughts have no power to help the troubling situations you see around you":
        case "Whenever you feel burdened or feel confronted with a difficult decision":
        case "Whenever you are tempted to attack yourself by condemning another":
        case "Whenever you feel tempted to make your own plans":
        case "Whenever you have attack thoughts, judgments, or make plans":
        case "Whenever you feel tempted to defend yourself":
        case "Whenever you encounter someone":
        case "When you are tempted to be afraid or to see a brother as a stranger":
        case "Whenever you are tempted to attack a brother":
        case "Whenever you are tempted to believe in a form of death":
        case "Whenever you are tempted to be sorrowful, fearful, tearful, or sick":
        case "Suggestion: Whenever you feel tempted to acknowledge death in any form\u2014as sorrow, anxiety, weariness, discomfort":
        case "Whenever you want to focus on someone\u2019s mistakes":
        case "When you are tempted to cherish an idol, a valueless thing of this world":
        case "Whenever you are tempted to believe you are making a sacrifice":
        case "Whenever you are tempted to blame the world for what you thought it did to you":
        case "Suggestion: Whenever you are tempted to think that the world causes your pain":
        case "Whenever you feel tempted to be angry":
        case "Whenever pain seems real, whenever you feel apprehension, care, terror, or distress":
        case "Whenever you feel tempted to become upset":
        case "Whenever you feel mercilessly pushed about by the world":
        case "Suggestion: Whenever you feel incensed because another is not sufficiently grateful to you":
        case "Suggestion: When tempted to succumb to any form of suffering or injury":
        case "Suggestion: When tempted to engage in any thoughts which reinforce a bodily identity":
        case "Suggestion: When tempted to seek peace from anything of this world":
        case "Suggestion: Whenever you are tempted to be upset":
        case "Whenever your peace is disturbed":
        case "Whenever ego thoughts enter your mind and direct your words and actions":
        case "Whenever illusions call us to stay and linger in a dream":
        case "Whenever you give way to a disturbance":
            return "Temptation"
        default:
            return expectNeverThrow(t, new Error(`'${t}' not a valid time descriptor.`))
    }
}

// @ts-expect-error debug
globalThis.testCrunch = () => {
    const practices = [...practiceLessonMap.values()].flatMap((practice) => practice.practices)
    console.log(practices.map((val) => val.time && timeCrunchMachine(val.time)))
}
